import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Country, State } from "country-state-city";
import { truncateCommaSeparatedString } from "../../components/common/utlity/utility";
import numberToWord from "../../components/common/utlity/invoiceword";
import { AWS_URL } from "../../services/http.service";
import { getCurrencySymbol } from "../../components/common/utlity/options";
import { useSelector } from "react-redux";

const formatCurrency = (val, currency) => {
  let num = 0;
  if (typeof val === "number") {
    num = val;
  } else if (typeof val === "string") {
    num = parseFloat(val);
    if (isNaN(num)) num = 0;
  }
  return `${getCurrencySymbol(currency)} ${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (str) => {
  if (!str) return "-";
  const date = new Date(str);
  if (isNaN(date)) return str;
  return date.toLocaleDateString();
};

const ModernInvoice = ({
  item,
  containerRef,
  businessName,
  phone,
  darkMode = false,
  bussiness,
  selectedColor = "bg-blue-500",
}) => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.user.user);
  const [pages, setPages] = useState([]);
  const pageRefs = useRef([]);

  // Dynamic Pagination Logic
  useEffect(() => {
    if (!item || !item.items) {
      setPages([]);
      return;
    }

    const calculatePages = () => {
      const chunks = [];
      let currentChunk = [];
      let currentHeight = 0;

      const itemsToUse = item?.invoiceId?.startsWith("RT")
        ? item?.returnitems
        : item?.items;
      const hsnSummary = itemsToUse?.reduce((acc, item) => {
        const hsn = item.hsnNumber || item.hsn || "N/A";
        const taxableValue =
          Number(item.quantity) * Number(item.salePrice) || 0;
        const isNoGSTItem =
          item?.showGST === false ||
          (bussiness?.state === item?.state &&
            (!item.cgst || parseFloat(item.cgst) === 0) &&
            (!item.sgst_utgst || parseFloat(item.sgst_utgst) === 0)) ||
          (bussiness?.state !== item?.state &&
            (!item.igst || parseFloat(item.igst) === 0));
        let totalGstRate = 0;
        let taxAmount = 0;
        if (!isNoGSTItem) {
          if (bussiness?.state === item?.state) {
            totalGstRate =
              parseFloat(item.cgst || 0) + parseFloat(item.sgst_utgst || 0);
          } else {
            totalGstRate = parseFloat(item.igst || 0);
          }
          taxAmount = (taxableValue * totalGstRate) / 100;
        }
        if (!acc[hsn]) {
          acc[hsn] = {
            hsn: hsn,
            taxableValue: 0,
            cgstRate:
              bussiness?.state === item?.state && !isNoGSTItem
                ? parseFloat(item.cgst || 0)
                : 0,
            sgstRate:
              bussiness?.state === item?.state && !isNoGSTItem
                ? parseFloat(item.sgst_utgst || 0)
                : 0,
            igstRate:
              bussiness?.state !== item?.state && !isNoGSTItem
                ? parseFloat(item.igst || 0)
                : 0,
            cessRate: parseFloat(item.cess || 0),
            cgstAmount: 0,
            sgstAmount: 0,
            igstAmount: 0,
            cessAmount: 0,
            totalTax: 0,
            totalGstRate: 0,
            isNoGST: isNoGSTItem,
          };
        }
        acc[hsn].taxableValue += taxableValue;
        acc[hsn].totalTax += taxAmount;
        acc[hsn].totalGstRate = totalGstRate;
        if (!isNoGSTItem) {
          if (bussiness?.state === item?.state) {
            acc[hsn].cgstAmount +=
              (taxableValue * parseFloat(item.cgst || 0)) / 100;
            acc[hsn].sgstAmount +=
              (taxableValue * parseFloat(item.sgst_utgst || 0)) / 100;
          } else {
            acc[hsn].igstAmount +=
              (taxableValue * parseFloat(item.igst || 0)) / 100;
          }
        }
        acc[hsn].cessAmount +=
          (taxableValue * parseFloat(item.cess || 0)) / 100;
        return acc;
      }, {});
      const hsnSummaryArray = Object.values(hsnSummary || {});

      // Estimate heights (in pixels, approximate based on typical rendering)
      const headerHeight = 150;
      const billingDetailsHeight = 100;
      const tableHeaderHeight = 40;
      const rowHeight = 50;
      const totalsHeight = 150;
      const hsnSummaryHeight = hsnSummaryArray.length * 50 + 50;
      const summaryAndTermsHeight = 200;
      const footerHeight = 100;
      const pageHeight = 1050; // Approx A4 height in pixels

      // Add fixed sections for first page
      currentHeight += headerHeight + billingDetailsHeight + tableHeaderHeight;

      itemsToUse.forEach((itm, index) => {
        const itemHeight = rowHeight;
        if (currentHeight + itemHeight > pageHeight) {
          chunks.push(currentChunk);
          currentChunk = [];
          currentHeight = tableHeaderHeight; // Reset for new page, no header or billing details
        }
        currentChunk.push(itm);
        currentHeight += itemHeight;
      });

      if (currentChunk.length > 0) {
        chunks.push(currentChunk);
      }

      // Adjust last page
      const lastPageIndex = chunks.length - 1;
      if (lastPageIndex >= 0) {
        const lastPageHeight =
          chunks[lastPageIndex].length * rowHeight +
          totalsHeight +
          hsnSummaryHeight +
          summaryAndTermsHeight +
          footerHeight;
        if (lastPageHeight > pageHeight && chunks[lastPageIndex].length > 1) {
          const lastChunk = chunks[lastPageIndex];
          const splitIndex = Math.floor(lastChunk.length / 2);
          chunks[lastPageIndex] = lastChunk.slice(0, splitIndex);
          chunks.push(lastChunk.slice(splitIndex));
        }
      }

      setPages(chunks);
    };

    calculatePages();
  }, [item, bussiness]);

  if (!item) return <div>No invoice data available</div>;

  const country = Country.getCountryByCode(bussiness?.country);
  const state = State.getStateByCodeAndCountry(
    bussiness?.state,
    bussiness?.country
  );
  const itemCountry = Country.getCountryByCode(item?.country);
  const itemState = State.getStateByCodeAndCountry(item?.state, item?.country);
  const isIntraState = bussiness?.state === item?.state;

  const calculateItemAmount = (item) => {
    const quantity = Number(item.quantity) || 0;
    const salePrice = Number(item.salePrice) || 0;
    const discountValue = parseFloat(item.discount || 0);
    const discountUnit = item.discountUnit || "%";
    const itemTotal = quantity * salePrice;
    let discountAmount = 0;
    if (
      discountUnit === "₹" ||
      discountUnit === "INR" ||
      discountUnit === "amount"
    ) {
      discountAmount = discountValue;
    } else {
      discountAmount = (itemTotal * discountValue) / 100;
    }
    return itemTotal - discountAmount;
  };

  const itemsToUse = item?.invoiceId?.startsWith("RT")
    ? item?.returnitems
    : item?.items;
  const totalQuantity =
    itemsToUse?.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0) || 0;
  const totalAmount =
    itemsToUse?.reduce(
      (sum, i) => sum + (Number(i.quantity) * Number(i.salePrice) || 0),
      0
    ) || 0;
  const paidAmount =
    (parseFloat(item.transactionAmount) || 0) -
    (parseFloat(item.remainingTransactionAmount) || 0);
  const remainingBalance = item.remainingTransactionAmount || 0;

  const totalItemDiscount =
    itemsToUse?.reduce((sum, i) => {
      const discountValue = parseFloat(i.discount || 0);
      const discountUnit = i.discountUnit || "%";
      const quantity = parseFloat(i.quantity || 0);
      const salePrice = parseFloat(i.salePrice || 0);
      const itemTotal = quantity * salePrice;
      let discountAmount = 0;
      if (
        discountUnit === "₹" ||
        discountUnit === "INR" ||
        discountUnit === "amount"
      ) {
        discountAmount = discountValue;
      } else {
        discountAmount = (itemTotal * discountValue) / 100;
      }
      return sum + discountAmount;
    }, 0) || 0;

  const totalDiscount = totalItemDiscount;
  const isNonGSTInvoice = item?.showGST === false;
  const isReturnInvoice = item?.invoiceId?.startsWith("RT");
  const isEstimate = item?.saleType === "estimate";
  const isAdvancePayment = item?.saleType === "advancePayment";

  const hsnSummaryArray = Object.values(
    itemsToUse?.reduce((acc, item) => {
      const hsn = item.hsnNumber || item.hsn || "N/A";
      const taxableValue = Number(item.quantity) * Number(item.salePrice) || 0;
      const isNoGSTItem =
        isNonGSTInvoice ||
        (isIntraState &&
          (!item.cgst || parseFloat(item.cgst) === 0) &&
          (!item.sgst_utgst || parseFloat(item.sgst_utgst) === 0)) ||
        (!isIntraState && (!item.igst || parseFloat(item.igst) === 0));
      let totalGstRate = 0;
      let taxAmount = 0;
      if (!isNoGSTItem) {
        if (isIntraState) {
          totalGstRate =
            parseFloat(item.cgst || 0) + parseFloat(item.sgst_utgst || 0);
        } else {
          totalGstRate = parseFloat(item.igst || 0);
        }
        taxAmount = (taxableValue * totalGstRate) / 100;
      }
      if (!acc[hsn]) {
        acc[hsn] = {
          hsn: hsn,
          taxableValue: 0,
          cgstRate:
            isIntraState && !isNoGSTItem ? parseFloat(item.cgst || 0) : 0,
          sgstRate:
            isIntraState && !isNoGSTItem ? parseFloat(item.sgst_utgst || 0) : 0,
          igstRate:
            !isIntraState && !isNoGSTItem ? parseFloat(item.igst || 0) : 0,
          cessRate: parseFloat(item.cess || 0),
          cgstAmount: 0,
          sgstAmount: 0,
          igstAmount: 0,
          cessAmount: 0,
          totalTax: 0,
          totalGstRate: 0,
          isNoGST: isNoGSTItem,
        };
      }
      acc[hsn].taxableValue += taxableValue;
      acc[hsn].totalTax += taxAmount;
      acc[hsn].totalGstRate = totalGstRate;
      if (!isNoGSTItem) {
        if (isIntraState) {
          acc[hsn].cgstAmount +=
            (taxableValue * parseFloat(item.cgst || 0)) / 100;
          acc[hsn].sgstAmount +=
            (taxableValue * parseFloat(item.sgst_utgst || 0)) / 100;
        } else {
          acc[hsn].igstAmount +=
            (taxableValue * parseFloat(item.igst || 0)) / 100;
        }
      }
      acc[hsn].cessAmount += (taxableValue * parseFloat(item.cess || 0)) / 100;
      return acc;
    }, {}) || {}
  );

  const totalTaxAmount = hsnSummaryArray.reduce(
    (sum, hsn) => sum + hsn.totalTax + hsn.cessAmount,
    0
  );
  const grandTotalBeforeDiscount = totalAmount + totalTaxAmount;
  const grandTotal = grandTotalBeforeDiscount - totalDiscount;
  const amountInwords = numberToWord(grandTotal);

  let currentAmount = grandTotal;
  const totalAdditionalDiscount = Array.isArray(item.additionalDiscount)
    ? item.additionalDiscount.reduce((sum, d) => {
        const amount = parseFloat(d.amount) || 0;
        let discountAmount = 0;
        if (d.type === "discount") {
          discountAmount = (currentAmount * amount) / 100;
        } else {
          discountAmount = amount;
        }
        currentAmount -= discountAmount;
        return sum + discountAmount;
      }, 0)
    : 0;

  const totalAdditionalFee = Array.isArray(item.additionalFee)
    ? item.additionalFee.reduce((sum, d) => {
        const amount = parseFloat(d.amount) || 0;
        let feeAmount = 0;
        if (d.type === "discount") {
          feeAmount = (currentAmount * amount) / 100;
        } else {
          feeAmount = amount;
        }
        currentAmount += feeAmount;
        return sum + feeAmount;
      }, 0)
    : 0;

  const isEntireInvoiceNoGST =
    isNonGSTInvoice || hsnSummaryArray.every((hsn) => hsn.isNoGST);

  // Render Header
  const renderHeader = () => (
    <div
      className={`flex justify-between items-start mb-6 border-b-2 pb-4 ${selectedColor}`}
    >
      <div className='flex items-center'>
        <img
          src={
            bussiness?.businesslogo
              ? AWS_URL + bussiness.businesslogo
              : "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT3dSEtDvpd9KAn4SdFJVoL0K1lj4Xfc-wVWQ&s"
          }
          alt='Business Logo'
          className='w-16 h-16 rounded-full object-contain mr-4'
          // crossOrigin='anonymous'
        />
        <div>
          <h1 className='text-2xl font-bold'>
            {businessName || "Business Name"}
          </h1>
          {!isNonGSTInvoice && (
            <p className='text-sm'>GSTIN: {bussiness.gstNumber || "N/A"}</p>
          )}
          <p className='text-sm'>Email: {bussiness?.email || "N/A"}</p>
          <p className='text-sm'>
            Address:{" "}
            {`${bussiness.firstLine}, ${state?.name}, ${country?.name}` ||
              "N/A"}
          </p>
        </div>
      </div>
      <div className='text-right'>
        {isReturnInvoice ? (
          <h2 className='text-xl font-bold'>Return Invoice/Debit Note</h2>
        ) : isEstimate ? (
          <h2 className='text-xl font-bold'>Estimate Invoice</h2>
        ) : (
          <h2 className='text-xl font-bold'>Tax Invoice</h2>
        )}
        {isNonGSTInvoice && !isEstimate && (
          <p
            className={`text-xs font-semibold ${
              darkMode ? "text-red-400" : "text-red-600"
            }`}
          >
            This is a Non-GST Invoice; cannot use for GST Returns
          </p>
        )}
        {isAdvancePayment && (
          <p
            className={`text-xs font-bold ${
              darkMode ? "text-blue-400" : "text-blue-600"
            }`}
          >
            This is a Proforma Invoice
          </p>
        )}
      </div>
    </div>
  );

  // Render Billing Details
  const renderBillingDetails = () => (
    <div className='grid grid-cols-2 gap-6 mb-6'>
      <div>
        <h3 className='text-lg font-semibold'>{t("NEWINVOICE.BILLINGTO")}</h3>
        <p className='font-medium'>{item.customerName || "-"}</p>
        <p>
          {t("NEWINVOICE.CONTACT_NO")}: {item.mobile || "-"}
        </p>
        <p className='text-sm'>
          {`${item.address}, ${itemState?.name}, ${itemCountry?.name}` || "N/A"}
        </p>
        <p
          className={`text-sm font-semibold ${
            darkMode ? "text-amber-400" : "text-amber-600"
          }`}
        >
          {isEntireInvoiceNoGST
            ? "No-GST Supply"
            : isIntraState
            ? "Intra-State Supply"
            : "Inter-State Supply"}
        </p>
      </div>
      <div className='text-right'>
        <p className='font-semibold'>
          {t("NEWINVOICE.NO")}: {item.invoiceId || "-"}
        </p>
        <p>
          {t("NEWINVOICE.DATE")}: {formatDate(item.saleDate)}
        </p>
        {item?.ewayBillDate && (
          <p className='text-sm'>Eway Bill Date: {item.ewayBillDate}</p>
        )}
        {item?.ewayBillNo && (
          <p className='text-sm'>Eway Bill No: {item.ewayBillNo}</p>
        )}
        {item?.validUpto && (
          <p className='text-sm'>Valid Upto: {item.validUpto}</p>
        )}
        {item?.committedDateToPay && (
          <p
            className={`text-sm font-bold ${
              darkMode ? "text-red-400" : "text-red-600"
            }`}
          >
            {t("COMMON.PAYMENT_DUE_DATE")}:{" "}
            {formatDate(item.committedDateToPay)}
          </p>
        )}
      </div>
    </div>
  );

  // Render Items Table Header
  const renderTableHeader = () => (
    <thead>
      <tr
        className={`${
          darkMode ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-900"
        }`}
      >
        <th className='py-2 px-4 border font-medium text-left'>Sl No.</th>
        <th className='py-2 px-4 border font-medium text-left'>Description</th>
        <th className='py-2 px-4 border font-medium text-center'>HSN/SAC</th>
        {!isEntireInvoiceNoGST && (
          <th className='py-2 px-4 border font-medium text-center'>GST Rate</th>
        )}
        <th className='py-2 px-4 border font-medium text-center'>Qty</th>
        <th className='py-2 px-4 border font-medium text-center'>Rate</th>
        <th className='py-2 px-4 border font-medium text-center'>Unit</th>
        <th className='py-2 px-4 border font-medium text-center'>Discount</th>
        <th className='py-2 px-4 border font-medium text-right'>Amount</th>
      </tr>
    </thead>
  );

  // Render Items Table Body for a chunk
  const renderTableBody = (chunk, startIdx) => (
    <tbody>
      {chunk.map((itm, chunkIdx) => {
        const idx = startIdx + chunkIdx;
        const itemAmount = Number(itm.quantity) * Number(itm.salePrice) || 0;
        const discountRate = parseFloat(itm.discount || 0);
        const discountUnit = itm.discountUnit || "%";
        let discountAmount = 0;
        if (
          discountUnit === "₹" ||
          discountUnit === "INR" ||
          discountUnit === "amount"
        ) {
          discountAmount = discountRate;
        } else {
          discountAmount = (itemAmount * discountRate) / 100;
        }
        const isNoGSTItem =
          isNonGSTInvoice ||
          (isIntraState &&
            (!itm.cgst || parseFloat(itm.cgst) === 0) &&
            (!itm.sgst_utgst || parseFloat(itm.sgst_utgst) === 0)) ||
          (!isIntraState && (!itm.igst || parseFloat(itm.igst) === 0));
        const gstRate = isIntraState
          ? parseFloat(itm.cgst || 0) + parseFloat(itm.sgst_utgst || 0)
          : parseFloat(itm.igst || 0);
        const gstAmount = !isNoGSTItem ? (itemAmount * gstRate) / 100 : 0;

        return (
          <tr
            key={idx}
            className={`${
              idx % 2 === 0
                ? darkMode
                  ? "bg-gray-800"
                  : "bg-white"
                : darkMode
                ? "bg-gray-700"
                : "bg-gray-50"
            }`}
          >
            <td className='py-2 px-4 border text-center'>{idx + 1}</td>
            <td className='py-2 px-4 border font-semibold'>
              {itm.productName || itm.description || "-"}
            </td>
            <td className='py-2 px-4 border text-center'>
              {truncateCommaSeparatedString(itm.hsnNumber) || itm.hsn || "N/A"}
            </td>
            {!isEntireInvoiceNoGST && (
              <td className='py-2 px-4 border text-center'>
                {!isNoGSTItem ? (
                  <>
                    <div className='text-xs'>({gstRate}%)</div>
                    <div>{formatCurrency(gstAmount, bussiness?.currency)}</div>
                    {parseFloat(itm.cess || 0) > 0 && (
                      <div className='text-xs text-amber-600'>
                        Cess: {itm.cess}% (
                        {formatCurrency(
                          (itemAmount * parseFloat(itm.cess || 0)) / 100,
                          bussiness?.currency
                        )}
                        )
                      </div>
                    )}
                  </>
                ) : (
                  <div className='text-xs text-gray-500'>No GST</div>
                )}
              </td>
            )}
            <td className='py-2 px-4 border text-center'>
              {itm.quantity || 0}
            </td>
            <td className='py-2 px-4 border text-right'>
              {formatCurrency(itm.salePrice, bussiness?.currency)}
            </td>
            <td className='py-2 px-4 border text-center'>
              {itm.unitSize || "Pcs"}
            </td>
            <td className='py-2 px-4 border text-center'>
              {discountRate > 0 ? (
                <>
                  <div>
                    {discountUnit === "₹" ||
                    discountUnit === "INR" ||
                    discountUnit === "amount"
                      ? formatCurrency(discountRate, bussiness?.currency)
                      : `${discountRate}%`}
                  </div>
                  <div className='text-red-600 text-xs'>
                    -{formatCurrency(discountAmount, bussiness?.currency)}
                  </div>
                </>
              ) : (
                "-"
              )}
            </td>
            <td className='py-2 px-4 border text-right'>
              {formatCurrency(itemAmount, bussiness?.currency)}
            </td>
          </tr>
        );
      })}
    </tbody>
  );

  // Render Totals
  const renderTotals = () => (
    <div className='flex justify-end'>
      <table className='min-w-full border-collapse border border-gray-300 rounded-lg text-sm'>
        <tbody>
          <tr
            className={`font-semibold ${
              darkMode ? "bg-gray-800" : "bg-gray-100"
            }`}
          >
            <td className='py-2 px-4 border text-right'>Total Amount</td>
            <td className='py-2 px-4 border text-right'>
              {formatCurrency(totalAmount, bussiness?.currency)}
            </td>
          </tr>
          {!isEntireInvoiceNoGST && (
            <>
              {isIntraState &&
                hsnSummaryArray.reduce((sum, hsn) => sum + hsn.cgstAmount, 0) >
                  0 && (
                  <tr
                    className={`font-semibold ${
                      darkMode ? "bg-gray-800" : "bg-gray-100"
                    }`}
                  >
                    <td className='py-2 px-4 border text-right'>Total CGST</td>
                    <td className='py-2 px-4 border text-right'>
                      {formatCurrency(
                        hsnSummaryArray.reduce(
                          (sum, hsn) => sum + hsn.cgstAmount,
                          0
                        ),
                        bussiness?.currency
                      )}
                    </td>
                  </tr>
                )}
              {isIntraState &&
                hsnSummaryArray.reduce((sum, hsn) => sum + hsn.sgstAmount, 0) >
                  0 && (
                  <tr
                    className={`font-semibold ${
                      darkMode ? "bg-gray-800" : "bg-gray-100"
                    }`}
                  >
                    <td className='py-2 px-4 border text-right'>Total SGST</td>
                    <td className='py-2 px-4 border text-right'>
                      {formatCurrency(
                        hsnSummaryArray.reduce(
                          (sum, hsn) => sum + hsn.sgstAmount,
                          0
                        ),
                        bussiness?.currency
                      )}
                    </td>
                  </tr>
                )}
              {!isIntraState &&
                hsnSummaryArray.reduce((sum, hsn) => sum + hsn.igstAmount, 0) >
                  0 && (
                  <tr
                    className={`font-semibold ${
                      darkMode ? "bg-gray-800" : "bg-gray-100"
                    }`}
                  >
                    <td className='py-2 px-4 border text-right'>Total IGST</td>
                    <td className='py-2 px-4 border text-right'>
                      {formatCurrency(
                        hsnSummaryArray.reduce(
                          (sum, hsn) => sum + hsn.igstAmount,
                          0
                        ),
                        bussiness?.currency
                      )}
                    </td>
                  </tr>
                )}
              {hsnSummaryArray.reduce((sum, hsn) => sum + hsn.cessAmount, 0) >
                0 && (
                <tr
                  className={`font-semibold ${
                    darkMode ? "bg-gray-800" : "bg-gray-100"
                  }`}
                >
                  <td className='py-2 px-4 border text-right'>Cess</td>
                  <td className='py-2 px-4 border text-right'>
                    {formatCurrency(
                      hsnSummaryArray.reduce(
                        (sum, hsn) => sum + hsn.cessAmount,
                        0
                      ),
                      bussiness?.currency
                    )}
                  </td>
                </tr>
              )}
            </>
          )}
          <tr
            className={`font-semibold ${
              darkMode ? "bg-gray-800" : "bg-gray-100"
            }`}
          >
            <td className='py-2 px-4 border text-right text-green-600'>
              Amount before discount
            </td>
            <td className='py-2 px-4 border text-right text-green-600'>
              {formatCurrency(grandTotalBeforeDiscount, bussiness?.currency)}
            </td>
          </tr>
          {totalDiscount > 0 && (
            <tr
              className={`font-semibold ${
                darkMode ? "bg-gray-800" : "bg-gray-100"
              }`}
            >
              <td className='py-2 px-4 border text-right'>Total Discount</td>
              <td
                className={`py-2 px-4 border text-right ${
                  darkMode ? "text-red-400" : "text-red-600"
                }`}
              >
                -{formatCurrency(totalDiscount, bussiness?.currency)}
              </td>
            </tr>
          )}
          <tr className={`font-bold ${selectedColor} text-white`}>
            <td className='py-2 px-4 border text-right'>Grand Total</td>
            <td className='py-2 px-4 border text-right'>
              {formatCurrency(grandTotal, bussiness?.currency)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  // Render HSN Summary
  const renderHsnSummary = () => (
    <div className='mb-6 overflow-x-auto'>
      <h3 className='text-lg font-semibold mb-2'>HSN Wise Tax Summary</h3>
      <table className='min-w-full border-collapse border border-gray-300 rounded-lg text-sm'>
        <thead>
          <tr
            className={`${
              darkMode ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-900"
            }`}
          >
            <th className='py-2 px-4 border font-medium text-left'>HSN/SAC</th>
            <th className='py-2 px-4 border font-medium text-right'>
              Taxable Value
            </th>
            {isIntraState ? (
              <>
                <th className='py-2 px-4 border font-medium text-center'>
                  CGST
                </th>
                <th className='py-2 px-4 border font-medium text-center'>
                  SGST/UTGST
                </th>
              </>
            ) : (
              <th className='py-2 px-4 border font-medium text-center'>IGST</th>
            )}
            <th className='py-2 px-4 border font-medium text-center'>Cess</th>
            <th className='py-2 px-4 border font-medium text-center'>
              Total Tax Amount
            </th>
          </tr>
        </thead>
        <tbody>
          {hsnSummaryArray.map((hsn, idx) => (
            <tr
              key={idx}
              className={`${
                idx % 2 === 0
                  ? darkMode
                    ? "bg-gray-800"
                    : "bg-white"
                  : darkMode
                  ? "bg-gray-700"
                  : "bg-gray-50"
              }`}
            >
              <td className='py-2 px-4 border'>
                {truncateCommaSeparatedString(hsn.hsn) || "N/A"}
              </td>
              <td className='py-2 px-4 border text-right'>
                {formatCurrency(hsn.taxableValue, bussiness?.currency)}
              </td>
              {isIntraState ? (
                <>
                  <td className='py-2 px-4 border text-center'>
                    {hsn.isNoGST ? (
                      <div className='text-gray-500'>N/A</div>
                    ) : (
                      <>
                        <div>{hsn.cgstRate}%</div>
                        <div>
                          {formatCurrency(hsn.cgstAmount, bussiness?.currency)}
                        </div>
                      </>
                    )}
                  </td>
                  <td className='py-2 px-4 border text-center'>
                    {hsn.isNoGST ? (
                      <div className='text-gray-500'>N/A</div>
                    ) : (
                      <>
                        <div>{hsn.sgstRate}%</div>
                        <div>
                          {formatCurrency(hsn.sgstAmount, bussiness?.currency)}
                        </div>
                      </>
                    )}
                  </td>
                </>
              ) : (
                <td className='py-2 px-4 border text-center'>
                  {hsn.isNoGST ? (
                    <div className='text-gray-500'>N/A</div>
                  ) : (
                    <>
                      <div>{hsn.igstRate}%</div>
                      <div>
                        {formatCurrency(hsn.igstAmount, bussiness?.currency)}
                      </div>
                    </>
                  )}
                </td>
              )}
              <td className='py-2 px-4 border text-center'>
                <div>{hsn.cessRate}%</div>
                <div>{formatCurrency(hsn.cessAmount, bussiness?.currency)}</div>
              </td>
              <td className='py-2 px-4 border text-center'>
                <div>
                  {hsn.isNoGST ? hsn.cessRate : hsn.totalGstRate + hsn.cessRate}
                  %
                </div>
                <div>
                  {formatCurrency(
                    hsn.totalTax + hsn.cessAmount,
                    bussiness?.currency
                  )}
                </div>
              </td>
            </tr>
          ))}
          <tr className={`font-bold ${selectedColor} text-white`}>
            <td className='py-2 px-4 border'>Total</td>
            <td className='py-2 px-4 border text-right'>
              {formatCurrency(totalAmount, bussiness?.currency)}
            </td>
            {isIntraState ? (
              <>
                <td className='py-2 px-4 border text-center'>
                  {formatCurrency(
                    hsnSummaryArray.reduce(
                      (sum, hsn) => sum + hsn.cgstAmount,
                      0
                    ),
                    bussiness?.currency
                  )}
                </td>
                <td className='py-2 px-4 border text-center'>
                  {formatCurrency(
                    hsnSummaryArray.reduce(
                      (sum, hsn) => sum + hsn.sgstAmount,
                      0
                    ),
                    bussiness?.currency
                  )}
                </td>
              </>
            ) : (
              <td className='py-2 px-4 border text-center'>
                {formatCurrency(
                  hsnSummaryArray.reduce((sum, hsn) => sum + hsn.igstAmount, 0),
                  bussiness?.currency
                )}
              </td>
            )}
            <td className='py-2 px-4 border text-center'>
              {formatCurrency(
                hsnSummaryArray.reduce((sum, hsn) => sum + hsn.cessAmount, 0),
                bussiness?.currency
              )}
            </td>
            <td className='py-2 px-4 border text-center'>
              {formatCurrency(totalTaxAmount, bussiness?.currency)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  // Render Summary and Terms
  const renderSummaryAndTerms = () => (
    <div className='grid grid-cols-2 gap-6 mb-6'>
      <div>
        <div className='mb-4'>
          <h3 className='text-sm font-semibold'>
            Amount Chargeable (in words)
          </h3>
          <p
            className={`text-xs italic ${
              darkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            {numberToWord(grandTotal)} Rupees only
          </p>
        </div>
        {!isEntireInvoiceNoGST && (
          <div className='mb-4'>
            <h3 className='text-sm font-semibold'>Tax Amount (in words)</h3>
            <p
              className={`text-xs italic ${
                darkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              {numberToWord(totalTaxAmount)} Rupees only
            </p>
          </div>
        )}
        <div className='border-t border-gray-300 pt-4'>
          <h3 className='text-sm font-semibold mb-2'>Terms & Declaration</h3>
          <div
            className={`text-xs space-y-2 ${
              darkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            <p>
              <strong>Declaration:</strong>
            </p>
            <ul className='list-disc list-inside space-y-1'>
              <li>
                We declare that this invoice shows the actual price of the goods
                described
              </li>
              <li>
                All particulars shown are true and correct to the best of our
                knowledge
              </li>
            </ul>
            <p>
              <strong>Terms:</strong>
            </p>
            <ul className='list-disc list-inside space-y-1'>
              <li>Goods once sold will not be taken back</li>
              <li>Payment due within 30 days from invoice date</li>
            </ul>
          </div>
        </div>
      </div>
      <div>
        <table className='w-full border-collapse border border-gray-300 text-sm max-w-xs ml-auto'>
          <tbody>
            <tr
              className={`${
                darkMode ? "bg-gray-800" : "bg-gray-100"
              } font-bold`}
            >
              <td className='py-1 px-3 border font-medium'>
                {t("NEWINVOICE.SUB_TOTAL")}
              </td>
              <td className='py-1 px-3 border'>
                {formatCurrency(grandTotal, bussiness?.currency)}
              </td>
            </tr>
            {totalAdditionalDiscount > 0 && (
              <tr>
                <td className='py-1 px-3 border font-medium'>
                  Additional Discounts
                  {item.additionalDiscount?.map((discount, idx) => (
                    <div key={idx} className='text-xs font-normal'>
                      • {discount.label}: {discount.amount}{" "}
                      {discount.type === "discount" ? "%" : "₹"}
                    </div>
                  ))}
                </td>
                <td className='py-1 px-3 border'>
                  -
                  {formatCurrency(totalAdditionalDiscount, bussiness?.currency)}
                </td>
              </tr>
            )}
            {totalAdditionalFee > 0 && (
              <tr>
                <td className='py-1 px-3 border font-medium'>
                  Additional Fees
                  {item.additionalFee?.map((fee, idx) => (
                    <div key={idx} className='text-xs font-normal'>
                      • {fee.label}: {fee.amount}{" "}
                      {fee.type === "discount" ? "%" : "₹"}
                    </div>
                  ))}
                </td>
                <td className='py-1 px-3 border'>
                  +{formatCurrency(totalAdditionalFee, bussiness?.currency)}
                </td>
              </tr>
            )}
            {item.subscriptionDisc > 0 && (
              <tr>
                <td className='py-1 px-3 border font-medium'>
                  {t("INVOICE.SUBSCRIPTION_SAVINGS")}
                </td>
                <td className='py-1 px-3 border'>
                  -{formatCurrency(item.subscriptionDisc, bussiness?.currency)}
                </td>
              </tr>
            )}
            <tr
              className={`border-t-2 ${
                darkMode ? "bg-gray-800" : "bg-gray-100"
              } font-bold`}
            >
              <td className='py-1 px-3 border'>{t("COMMON.TOTAL_AMOUNT")}</td>
              <td className='py-1 px-3 border'>
                {formatCurrency(
                  grandTotal -
                    totalAdditionalDiscount +
                    totalAdditionalFee -
                    (item.subscriptionDisc || 0),
                  bussiness?.currency
                )}
              </td>
            </tr>
            {(isAdvancePayment || item?.saleType === "advanceSale") && (
              <tr>
                <td className='py-1 px-3 border font-medium'>Advance Amount</td>
                <td className='py-1 px-3 border'>
                  {formatCurrency(item.advancePayment, bussiness?.currency)}
                </td>
              </tr>
            )}
            <tr>
              <td className='py-1 px-3 border font-medium'>
                {t("NEWINVOICE.PAYMENT_MODE")}
              </td>
              <td className='py-1 px-3 border'>{item.paymentMode || "-"}</td>
            </tr>
            <tr>
              <td className='py-1 px-3 border font-medium'>
                {isAdvancePayment
                  ? "Remaining Balance"
                  : t("COMMON.TOTAL_VALUE")}
              </td>
              <td className='py-1 px-3 border'>
                {isReturnInvoice
                  ? formatCurrency(item.totalReturnAmount, bussiness?.currency)
                  : formatCurrency(
                      grandTotal -
                        totalAdditionalDiscount +
                        totalAdditionalFee -
                        (item.subscriptionDisc || 0) -
                        parseFloat(item.advancePayment || 0),
                      bussiness?.currency
                    )}
              </td>
            </tr>
            {item.remainingTransactionAmount > 0 && (
              <>
                <tr>
                  <td className='py-1 px-3 border font-medium'>
                    {t("NEWINVOICE.RECEIVED")}
                  </td>
                  <td className='py-1 px-3 border'>
                    {formatCurrency(paidAmount, bussiness?.currency)}
                  </td>
                </tr>
                <tr>
                  <td className='py-1 px-3 border font-bold'>Due Balance</td>
                  <td className='py-1 px-3 border font-bold'>
                    {formatCurrency(remainingBalance, bussiness?.currency)}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
        <div className='mt-6 p-4 bg-gray-100 rounded-lg text-center'>
          {bussiness?.signature ? (
            <img
              src={AWS_URL + bussiness.signature}
              alt='Signature'
              className='w-32 h-20 object-contain mx-auto'
              // crossOrigin='anonymous'
            />
          ) : (
            <span className='text-gray-400 text-xs'>Signature</span>
          )}
          <p className='mt-2 font-medium'>
            {t("NEWINVOICE.AUTHORIZED_SIGNATORY")}
          </p>
        </div>
      </div>
    </div>
  );

  // Render Footer
  const renderFooter = () => (
    <div className='border-t border-gray-300 pt-4'>
      <div className='grid grid-cols-3 gap-2'>
        <div className='col-span-2'>
          <div className='mb-4'>
            <h3 className='text-lg font-semibold'>
              {t("NEWINVOICE.AMOUNT_IN_WORDS")}
            </h3>
            <p
              className={`text-sm italic ${
                darkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              {amountInwords} Rupees only
            </p>
          </div>
          {item.notes && (
            <div className='mb-4'>
              <h3 className='text-lg font-semibold'>Notes</h3>
              <p className={darkMode ? "text-gray-400" : "text-gray-600"}>
                {item.notes}
              </p>
            </div>
          )}
          <p className={darkMode ? "text-gray-400" : "text-gray-600"}>
            {t("NEWINVOICE.FOR_COMPANY")}: {bussiness.businessName}
          </p>
          <p className={darkMode ? "text-gray-400" : "text-gray-600"}>
            {item.authorizedPerson || ""}
          </p>
        </div>
        {bussiness?.aadharPhoto && (
          <div className='flex justify-center'>
            <img
              src={AWS_URL + bussiness?.aadharPhoto}
              alt='Payment QR Code'
              className='w-24 object-contain'
              // crossOrigin='anonymous'
            />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div
      ref={containerRef}
      className={` ${
        darkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"
      }`}
    >
      <style>{`
        @media print {
          .page {
            page-break-after: always;
            min-height: auto;
          }
          .page:not(:first-child) {
            padding-top: 160mm; /* Matches approximate height of header (150px) plus billing details (100px) plus some margin */
          }
        }
      `}</style>
      {pages.length === 0 ? (
        <div>No invoice data available</div>
      ) : (
        pages.map((chunk, pageIndex) => (
          <div
            key={pageIndex}
            ref={(el) => (pageRefs.current[pageIndex] = el)}
            className='page'
            style={{
              width: "210mm",
              minHeight: "297mm",
              padding: "10mm",
              boxSizing: "border-box",
            }}
          >
            {pageIndex === 0 && renderHeader()}
            {pageIndex === 0 && renderBillingDetails()}
            <div className='mb-6 overflow-x-auto'>
              <table className='min-w-full border-collapse border border-gray-300 rounded-lg text-sm'>
                {renderTableHeader()}
                {renderTableBody(chunk, pageIndex * 15)}{" "}
                {/* Assuming maxRowsPerPage = 15 */}
              </table>
            </div>
            {pageIndex === pages.length - 1 && renderTotals()}
            {pageIndex === pages.length - 1 &&
              !isEntireInvoiceNoGST &&
              renderHsnSummary()}
            {pageIndex === pages.length - 1 && renderSummaryAndTerms()}
            {pageIndex === pages.length - 1 && renderFooter()}
          </div>
        ))
      )}
    </div>
  );
};

export default ModernInvoice;
